import { theme } from '@metorial/ui';
import { RiCornerDownLeftLine, RiSearchLine } from '@remixicon/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

// "Search in BlackOS" (⌘K): a local command palette over pages and actions.
// No federation search; everything it lists comes from the app's navigation.

export interface CommandPaletteItem {
  id: string;
  label: string;
  group?: string;
  description?: string;
  icon?: React.ReactNode;
  keywords?: string[];
  onSelect: () => void;
}

let isMac = () =>
  typeof navigator != 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');

export let commandPaletteShortcutLabel = () => (isMac() ? '⌘K' : 'Ctrl K');

/** Opens the palette on ⌘K / Ctrl+K. */
export let useCommandPaletteHotkey = (setOpen: (fn: (open: boolean) => boolean) => void) => {
  useEffect(() => {
    let onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() == 'k') {
        e.preventDefault();
        setOpen(open => !open);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);
};

let SearchTrigger = styled.button`
  height: 36px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px 0 12px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.gray400};
  background: ${theme.colors.gray200};
  color: ${theme.colors.gray650};
  font-size: 14px;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  span {
    flex-grow: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  kbd {
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 5px;
    border: 1px solid ${theme.colors.gray400};
    background: ${theme.colors.background};
    color: ${theme.colors.gray700};
  }

  &:hover,
  &:focus-visible {
    background: ${theme.colors.background};
    color: ${theme.colors.gray800};
  }
`;

export let CommandPaletteTrigger = ({
  placeholder,
  onClick
}: {
  placeholder: string;
  onClick: () => void;
}) => (
  <SearchTrigger type="button" onClick={onClick} aria-label={placeholder}>
    <RiSearchLine />
    <span>{placeholder}</span>
    <kbd>{commandPaletteShortcutLabel()}</kbd>
  </SearchTrigger>
);

let Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.18);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
`;

let Panel = styled.div`
  width: min(640px, calc(100vw - 32px));
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.gray400};
  border-radius: 14px;
  box-shadow: ${theme.shadows.large};
  overflow: hidden;
`;

let InputRow = styled.label`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 52px;
  border-bottom: 1px solid ${theme.colors.gray300};
  color: ${theme.colors.gray600};

  input {
    flex-grow: 1;
    border: none;
    outline: none;
    background: none;
    font-size: 15px;
    color: ${theme.colors.foreground};
  }
`;

let List = styled.div`
  overflow-y: auto;
  padding: 6px;
`;

let GroupLabel = styled.p`
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.gray600};
`;

let Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: none;
  text-align: left;
  cursor: pointer;
  color: ${theme.colors.gray800};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${theme.colors.gray650};
  }

  &[data-selected='true'] {
    background: ${theme.colors.gray300};
    color: ${theme.colors.foreground};
  }
`;

// Rows are buttons (line-height: 1 in the app reset), so set line-heights here:
// the label and its path must not touch.
let RowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex-grow: 1;
  min-width: 0;

  b {
    font-size: 14px;
    line-height: 19px;
    font-weight: 550;
  }

  small {
    font-size: 12px;
    line-height: 16px;
    color: ${theme.colors.gray650};
    overflow-wrap: anywhere;
  }
`;

let Empty = styled.p`
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  color: ${theme.colors.gray650};
`;

let matches = (item: CommandPaletteItem, query: string) => {
  let haystack = [item.label, item.group, item.description, ...(item.keywords ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(term => haystack.includes(term));
};

export let CommandPalette = ({
  open,
  onOpenChange,
  items,
  placeholder = 'Search pages and actions...'
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandPaletteItem[];
  placeholder?: string;
}) => {
  let [query, setQuery] = useState('');
  let [selected, setSelected] = useState(0);
  let inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  let filtered = useMemo(
    () => (query.trim() ? items.filter(item => matches(item, query)) : items),
    [items, query]
  );

  useEffect(() => setSelected(0), [query]);

  if (!open) return null;

  let choose = (item: CommandPaletteItem | undefined) => {
    if (!item) return;
    onOpenChange(false);
    item.onSelect();
  };

  let onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key == 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    } else if (e.key == 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, filtered.length - 1));
    } else if (e.key == 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key == 'Enter') {
      e.preventDefault();
      choose(filtered[selected]);
    }
  };

  let lastGroup: string | undefined;

  return (
    <Overlay onMouseDown={() => onOpenChange(false)}>
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={placeholder}
        onMouseDown={e => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <InputRow>
          <RiSearchLine size={18} />
          <input
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            onChange={e => setQuery(e.target.value)}
            aria-label={placeholder}
          />
        </InputRow>

        <List>
          {filtered.length == 0 && <Empty>No results for "{query}".</Empty>}

          {filtered.map((item, index) => {
            let showGroup = item.group && item.group != lastGroup;
            lastGroup = item.group;

            return (
              <React.Fragment key={item.id}>
                {showGroup && <GroupLabel>{item.group}</GroupLabel>}

                <Row
                  type="button"
                  data-selected={index == selected}
                  onMouseEnter={() => setSelected(index)}
                  onClick={() => choose(item)}
                >
                  {item.icon}
                  <RowText>
                    <b>{item.label}</b>
                    {item.description && <small>{item.description}</small>}
                  </RowText>
                  {index == selected && <RiCornerDownLeftLine />}
                </Row>
              </React.Fragment>
            );
          })}
        </List>
      </Panel>
    </Overlay>
  );
};
